import { Loader2, CheckCircle2, Plus, HardHat, Building2, Calendar } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { INPUT_CLS, ColourDot } from './constants';

export const StepPackage = ({
  step, mode, showAccessRequest,
  selectedProject,
  effectiveForm,
  contractorRole, setContractorRole,
  selectedContractorPackageId, setSelectedContractorPackageId,
  selectedSubContractorPackageId, setSelectedSubContractorPackageId,
  selectedContractorAccountId, setSelectedContractorAccountId,
  showNewPackage, setShowNewPackage,
  newContractorAccountQuery, setNewContractorAccountQuery,
  newContractorAccount, setNewContractorAccount,
  newContractorAccounts, newContractorAccountLoading,
  newPackageType, setNewPackageType,
  newPackageParentCP, setNewPackageParentCP,
  newPackageCategories, setNewPackageCategories,
  newPackageEnconStartDate, setNewPackageEnconStartDate,
  packagesLoading, projectPackages, packageTypes,
  packageTypesLoading, onRetryPackageTypes,
  mainContractorCount, subContractorCount,
  creatingPackage, createPackage,
  step3Ready, setStep,
}) => {
  if (step !== 2 || mode !== 'project' || showAccessRequest) return null;
  return (
    <>
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
        <div className="text-[11px] uppercase tracking-wider text-blue-700 font-bold">Project</div>
        <div className="font-semibold">{selectedProject?.deal_name}</div>
        <div className="text-xs text-gray-600">
          {[selectedProject?.barbour_project_id ? `Barbour #${selectedProject.barbour_project_id}` : null, selectedProject?.postcode].filter(Boolean).join(' · ')}
        </div>
      </div>

      {effectiveForm?.customer_name && !selectedContractorPackageId && !selectedSubContractorPackageId && !showNewPackage && (
        <div className="rounded-xl border-2 border-dashed border-amber-300 bg-amber-50/60 p-3 text-sm flex items-start gap-3" data-testid="crm-suggested-account-banner">
          <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
            <Building2 className="w-4 h-4 text-amber-700" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-wider text-amber-700 font-bold">Suggested from quote PDF</div>
            <div className="font-semibold text-gray-900 truncate">{effectiveForm.customer_name}</div>
            <div className="text-[11px] text-gray-500">Use this as the {contractorRole === 'main' ? 'main contractor' : 'sub contractor'}, or search to pick a different one.</div>
          </div>
          <Button
            size="sm"
            onClick={() => {
              setShowNewPackage(true);
              setSelectedContractorAccountId('');
              setNewContractorAccount(null);
              setNewContractorAccountQuery(effectiveForm.customer_name);
            }}
            className="bg-amber-500 hover:bg-amber-600 text-white shrink-0"
            data-testid="crm-suggested-account-use"
          >
            Use as contractor
          </Button>
        </div>
      )}

      <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
        <button
          onClick={() => { setContractorRole('main'); setSelectedSubContractorPackageId(''); setSelectedContractorAccountId(''); setShowNewPackage(false); }}
          className={`flex-1 py-2 rounded-md text-sm font-semibold transition-colors flex items-center justify-center gap-2 ${contractorRole === 'main' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          data-testid="crm-role-main"
        >
          <HardHat className="w-4 h-4" />
          <span>Main Contractor</span>
          <span
            className={`inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 text-[11px] font-bold rounded-full ${
              mainContractorCount > 0 ? (contractorRole === 'main' ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-700') : 'bg-gray-300 text-gray-600'
            }`}
            data-testid="crm-role-main-count"
          >
            {packagesLoading ? '…' : mainContractorCount}
          </span>
        </button>
        <button
          onClick={() => { setContractorRole('sub'); setSelectedContractorPackageId(''); setSelectedContractorAccountId(''); setShowNewPackage(false); }}
          className={`flex-1 py-2 rounded-md text-sm font-semibold transition-colors flex items-center justify-center gap-2 ${contractorRole === 'sub' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          data-testid="crm-role-sub"
        >
          <HardHat className="w-4 h-4" />
          <span>Sub Contractor</span>
          <span
            className={`inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 text-[11px] font-bold rounded-full ${
              subContractorCount > 0 ? (contractorRole === 'sub' ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-700') : 'bg-gray-300 text-gray-600'
            }`}
            data-testid="crm-role-sub-count"
          >
            {packagesLoading ? '…' : subContractorCount}
          </span>
        </button>
      </div>

      {contractorRole === 'main' && projectPackages.contractor_packages.length > 0 && !selectedContractorPackageId && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="crm-pkg-required-hint-main">
          Pick a Main Contractor package below — the quote will be linked under that package in Zoho.
        </div>
      )}
      {contractorRole === 'sub' && projectPackages.sub_contractor_packages.length > 0 && !selectedSubContractorPackageId && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="crm-pkg-required-hint-sub">
          Pick a Sub Contractor package below — the quote will be linked under that package in Zoho.
        </div>
      )}

      {(() => {
        const list = contractorRole === 'main' ? projectPackages.contractor_packages : projectPackages.sub_contractor_packages;
        const acctKey = contractorRole === 'main' ? 'contractor_account_id' : 'sub_contractor_account_id';
        const acctNameKey = contractorRole === 'main' ? 'contractor_account_name' : 'sub_contractor_account_name';
        const byAcct = {};
        list.forEach(pkg => {
          const aid = pkg[acctKey] || `__unk__${pkg.id}`;
          if (!byAcct[aid]) byAcct[aid] = { id: aid, name: pkg[acctNameKey] || pkg.name || 'Unknown', packages: [] };
          byAcct[aid].packages.push(pkg);
        });
        const contractors = Object.values(byAcct);
        const selectedContractor = contractors.find(c => c.id === selectedContractorAccountId);
        return (
          <>
            <div>
              <Label className="text-sm font-semibold">
                {contractorRole === 'main' ? 'Main Contractors' : 'Sub Contractors'} on this project
              </Label>
              <div className="border-2 border-gray-200 rounded-xl bg-white divide-y max-h-48 overflow-y-auto mt-1">
                {packagesLoading ? (
                  <div className="p-3 text-center text-gray-500 text-sm">Loading…</div>
                ) : contractors.length === 0 ? (
                  <div className="p-3 text-center text-gray-500 text-sm">
                    No {contractorRole === 'main' ? 'main' : 'sub'} contractors yet — add one below
                  </div>
                ) : (
                  contractors.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => { setSelectedContractorAccountId(c.id); setSelectedContractorPackageId(''); setSelectedSubContractorPackageId(''); setShowNewPackage(false); }}
                      className={`w-full text-left p-3 hover:bg-gray-50 flex items-center justify-between ${selectedContractorAccountId === c.id ? 'bg-emerald-50 border-l-4 border-emerald-600' : ''}`}
                      data-testid={`crm-contractor-option-${c.id}`}
                    >
                      <div>
                        <div className="font-semibold text-sm">{c.name}</div>
                        <div className="text-xs text-gray-500">{c.packages.length} package{c.packages.length === 1 ? '' : 's'}</div>
                      </div>
                      {selectedContractorAccountId === c.id && <CheckCircle2 className="w-5 h-5 text-emerald-600" />}
                    </button>
                  ))
                )}
              </div>
            </div>
            {selectedContractor && (
              <div className="border-2 border-blue-100 rounded-lg p-3 bg-blue-50/30 space-y-2">
                <Label className="text-xs font-bold text-blue-900">
                  Pick a package for <span className="underline">{selectedContractor.name}</span>
                </Label>
                <div className="border-2 border-gray-200 rounded-xl divide-y max-h-56 overflow-y-auto bg-white">
                  {selectedContractor.packages.map((pkg) => {
                    const active = (contractorRole === 'main' ? selectedContractorPackageId : selectedSubContractorPackageId) === pkg.id;
                    return (
                      <button
                        key={pkg.id}
                        onClick={() => { if (contractorRole === 'main') setSelectedContractorPackageId(pkg.id); else setSelectedSubContractorPackageId(pkg.id); }}
                        className={`w-full text-left p-3 hover:bg-gray-50 flex items-center justify-between ${active ? 'bg-emerald-50 border-l-4 border-emerald-600' : ''}`}
                        data-testid={`crm-pkg-option-${pkg.id}`}
                      >
                        <div>
                          <div className="font-semibold text-sm">{pkg.package_type || pkg.name}</div>
                          <div className="text-xs text-gray-500">
                            {[pkg.parent_contractor_package_name ? `under: ${pkg.parent_contractor_package_name}` : null, pkg.status, Array.isArray(pkg.package_category) && pkg.package_category.length ? pkg.package_category.join(', ') : null].filter(Boolean).join(' · ') || 'No status set'}
                          </div>
                        </div>
                        {active && <CheckCircle2 className="w-5 h-5 text-emerald-600" />}
                      </button>
                    );
                  })}
                </div>
                <div className="flex justify-start pt-1">
                  <Button variant="outline" size="sm"
                    onClick={() => { setShowNewPackage(true); setNewContractorAccount({ id: selectedContractor.id, name: selectedContractor.name }); setNewContractorAccountQuery(selectedContractor.name); }}
                    data-testid="crm-new-package-for-contractor-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" /> New package for {selectedContractor.name}
                  </Button>
                </div>
              </div>
            )}
          </>
        );
      })()}

      {!showNewPackage && (
        <div className="flex justify-start">
          <Button variant="outline" size="sm"
            onClick={() => { setShowNewPackage(true); setSelectedContractorAccountId(''); setNewContractorAccount(null); setNewContractorAccountQuery(''); }}
            data-testid="crm-new-contractor-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Add a new {contractorRole === 'main' ? 'main' : 'sub'} contractor
          </Button>
        </div>
      )}

      {showNewPackage && (
        <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50/30 space-y-3">
          <div className="font-semibold text-sm text-blue-900">
            New {contractorRole === 'main' ? 'Main' : 'Sub'} Contractor Package
          </div>
          <div>
            <Label className="text-xs">Contractor (search Accounts) *</Label>
            <Input
              value={newContractorAccountQuery}
              onChange={(e) => { setNewContractorAccountQuery(e.target.value); setNewContractorAccount(null); }}
              placeholder="e.g. Linear Projects Ltd"
              className={INPUT_CLS}
              data-testid="crm-new-pkg-contractor-search"
            />
            {!newContractorAccount && (newContractorAccountLoading || newContractorAccounts.length > 0) && (
              <div className="border-2 border-gray-200 rounded-xl max-h-40 overflow-y-auto mt-1 bg-white divide-y">
                {newContractorAccountLoading ? (
                  <div className="p-2 text-xs text-gray-500">Searching…</div>
                ) : (
                  newContractorAccounts.slice(0, 15).map((a) => (
                    <button key={a.id} onClick={() => { setNewContractorAccount(a); setNewContractorAccountQuery(a.name); }}
                      className="w-full text-left text-sm px-3 py-1.5 hover:bg-gray-50"
                      data-testid={`crm-new-pkg-contractor-opt-${a.id}`}
                    >
                      {a.name}
                      <span className="text-xs text-gray-500 ml-1">{[a.city, a.customer_code].filter(Boolean).join(' · ')}</span>
                    </button>
                  ))
                )}
              </div>
            )}
            {newContractorAccount && <p className="text-[11px] text-emerald-600 mt-1 font-semibold">✓ {newContractorAccount.name}</p>}
          </div>
          <div>
            <Label className="text-xs">Package Type *</Label>
            <Select value={newPackageType} onValueChange={setNewPackageType}>
              <SelectTrigger className={INPUT_CLS} data-testid="crm-new-pkg-type"><SelectValue placeholder={packageTypesLoading ? 'Loading…' : 'Pick a package type'} /></SelectTrigger>
              <SelectContent className="bg-white max-h-64 z-[120] rounded-xl border-gray-200 shadow-xl">
                {(contractorRole === 'main' ? packageTypes.contractor_package_types : packageTypes.sub_contractor_package_types).length === 0 ? (
                  <div className="px-3 py-2 text-xs text-gray-500 flex items-center justify-between gap-2">
                    <span>No types loaded</span>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onRetryPackageTypes?.(); }}
                      className="text-blue-600 underline text-xs"
                      data-testid="crm-pkg-type-retry"
                    >Retry</button>
                  </div>
                ) : (
                  (contractorRole === 'main' ? packageTypes.contractor_package_types : packageTypes.sub_contractor_package_types).map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5 text-gray-400" /> Encon Start Date
              {selectedProject?.barbour_start_date && (
                <span className="text-[10px] text-gray-400 font-normal">(defaults to Barbour: {selectedProject.barbour_start_date})</span>
              )}
            </Label>
            <Input
              type="date"
              value={newPackageEnconStartDate || ''}
              onChange={e => setNewPackageEnconStartDate(e.target.value)}
              className={INPUT_CLS}
              data-testid="crm-new-pkg-encon-start-date"
            />
          </div>
          {contractorRole === 'sub' && projectPackages.contractor_packages.length > 0 && (
            <div>
              <Label className="text-xs">Parent Main Contractor Package (optional)</Label>
              <Select value={newPackageParentCP} onValueChange={setNewPackageParentCP}>
                <SelectTrigger className={INPUT_CLS} data-testid="crm-new-pkg-parent"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent className="bg-white z-[120] rounded-xl border-gray-200 shadow-xl">
                  {projectPackages.contractor_packages.map((pkg) => (
                    <SelectItem key={pkg.id} value={pkg.id}>{pkg.contractor_account_name || pkg.name} — {pkg.package_type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {contractorRole === 'sub' && (packageTypes.sub_contractor_package_categories || []).length > 0 && (
            <div>
              <Label className="text-xs">Sub Contractor Package Category</Label>
              <Select value={newPackageCategories[0] || ''} onValueChange={(v) => setNewPackageCategories(v ? [v] : [])}>
                <SelectTrigger className={`mt-1 ${INPUT_CLS}`} data-testid="crm-new-pkg-categories"><SelectValue placeholder="Pick a category" /></SelectTrigger>
                <SelectContent className="bg-white z-[120] rounded-xl border-gray-200 shadow-xl max-h-72">
                  {(packageTypes.sub_contractor_package_categories || []).map((cat) => (
                    <SelectItem key={cat} value={cat} data-testid={`crm-new-pkg-cat-${cat.replace(/\s+/g, '-').toLowerCase()}`}>
                      <ColourDot color={(packageTypes.sub_contractor_package_categories_meta || []).find(m => m.value === cat)?.color || null} />
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowNewPackage(false)}>Cancel</Button>
            <Button size="sm" onClick={createPackage} disabled={creatingPackage} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="crm-new-pkg-submit">
              {creatingPackage ? (<><Loader2 className="w-4 h-4 animate-spin mr-1" />Creating…</>) : 'Create Package'}
            </Button>
          </div>
        </div>
      )}

      <div className="flex justify-between gap-2 pt-2">
        <Button variant="outline" onClick={() => setStep(1)}>← Back</Button>
        <Button onClick={() => setStep(3)} disabled={!step3Ready} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="crm-next-package-btn">
          Next: Quote Details
        </Button>
      </div>
    </>
  );
};
